import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, NavLink, useLocation } from 'react-router-dom';
import TrendingV2 from './TrendingV2';
import PreMarketV2 from './PreMarketV2';
import SocialV2 from './SocialV2';
import { SourcesProvider } from './sources_context';

const T = {
  bgBase: '#080b11',
  bgHeader: 'rgba(10,13,20,.82)',
  border1: '#1a2230',
  textHi: '#e7ecf3',
  textMid: '#c5cdd9',
  textMuted: '#8b94a3',
  green: '#34d399',
  mono: "'JetBrains Mono', ui-monospace, monospace",
};

function ComingSoonTab({ icon, name, message }) {
  return (
    <div style={{ maxWidth: 560, margin: '14vh auto', padding: 40, textAlign: 'center', animation: 'risein .4s ease' }}>
      <div style={{ fontSize: 54, marginBottom: 6 }}>{icon}</div>
      <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 10, color: T.textHi }}>{name}</div>
      <p style={{ color: T.textMuted, fontSize: 15, lineHeight: 1.6, margin: '0 0 24px' }}>{message}</p>
      <NavLink to="/v2/dashboard/monsters" style={{
        display: 'inline-block', padding: '11px 20px', borderRadius: 11,
        background: 'linear-gradient(180deg,#211c12,#171307)',
        border: '1px solid #3a3320', color: '#fbbf24',
        fontWeight: 700, fontSize: 14, textDecoration: 'none',
      }}>🔥 Go to Trending</NavLink>
    </div>
  );
}

function Header() {
  const location = useLocation();
  const [clock, setClock] = useState('');
  const [marketOpen, setMarketOpen] = useState(true);

  useEffect(() => {
    const update = () => {
      const fmt = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      });
      setClock(fmt.format(new Date()) + ' ET');
      const hr = parseInt(new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: 'numeric', hour12: false }).format(new Date()), 10);
      const min = parseInt(new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', minute: 'numeric' }).format(new Date()), 10);
      const t = hr * 60 + min;
      const day = new Date().getDay();
      setMarketOpen(day >= 1 && day <= 5 && t >= 9 * 60 + 30 && t < 16 * 60);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, []);

  const tabs = [
    { to: '/v2/dashboard/premarket', icon: '📊', label: 'Pre-Market' },
    { to: '/v2/dashboard/social', icon: '📱', label: 'Social Sentiment' },
    { to: '/v2/dashboard/monsters', icon: '🔥', label: 'Trending' },
  ];

  return (
    <header style={{
      position: 'sticky', top: 0, zIndex: 40,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
      padding: '0 24px', height: 60,
      background: T.bgHeader, backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
      borderBottom: `1px solid ${T.border1}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 22, lineHeight: 1 }}>🦔</span>
        <span className="hdr-brandtext" style={{ fontWeight: 700, fontSize: 16, letterSpacing: '-.01em', color: T.textHi }}>
          Hedgehog <span style={{ color: T.textMuted, fontWeight: 600 }}>Trading</span>
        </span>
        <style>{'@media (max-width:860px){ .hdr-brandtext,.hdr-status{display:none} .hdr-nav{overflow-x:auto;justify-content:flex-start;scrollbar-width:none;-webkit-overflow-scrolling:touch} .hdr-nav::-webkit-scrollbar{display:none} }'}</style>
      </div>
      <nav className="hdr-nav" style={{ display: 'flex', gap: 6 }}>
        {tabs.map((t) => {
          const active = location.pathname.startsWith(t.to);
          return (
            <NavLink key={t.to} to={t.to} style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '8px 14px', borderRadius: 10,
              border: `1px solid ${active ? 'rgba(129,140,248,.4)' : 'transparent'}`,
              background: active ? 'rgba(129,140,248,.12)' : 'transparent',
              color: active ? T.textHi : T.textMuted,
              fontSize: 13, fontWeight: 600, textDecoration: 'none',
              transition: 'all .15s',
            }}>
              <span style={{ fontSize: 14 }}>{t.icon}</span>{t.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="hdr-status" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#7f8a9a' }}>
          <span style={{
            width: 7, height: 7, borderRadius: '50%',
            background: marketOpen ? T.green : '#6b7585',
            boxShadow: marketOpen ? `0 0 8px ${T.green}` : 'none',
          }} />
          Market {marketOpen ? 'open' : 'closed'}
        </span>
        <span style={{
          fontFamily: T.mono, fontSize: 14, fontWeight: 600, color: T.textMid,
          fontVariantNumeric: 'tabular-nums', letterSpacing: '.02em',
        }}>{clock}</span>
      </div>
    </header>
  );
}

export default function AppV2() {
  // Override the v1 global '#root { height:100vh; overflow:hidden }' that clips v2 scroll
  React.useEffect(() => {
    const root = document.getElementById('root');
    const prev = root ? { h: root.style.height, ov: root.style.overflow } : null;
    if (root) { root.style.height = 'auto'; root.style.overflow = 'visible'; }
    const prevBody = { ov: document.body.style.overflow, h: document.body.style.height };
    document.body.style.overflow = 'auto'; document.body.style.height = 'auto';
    return () => {
      if (root && prev) { root.style.height = prev.h; root.style.overflow = prev.ov; }
      document.body.style.overflow = prevBody.ov; document.body.style.height = prevBody.h;
    };
  }, []);
  return (
    <SourcesProvider><div style={{
      minHeight: '100vh',
      background:
        'radial-gradient(1200px 600px at 80% -10%, rgba(99,102,241,.10), transparent 60%), ' +
        'radial-gradient(900px 500px at -5% 0%, rgba(245,158,11,.07), transparent 55%), ' + T.bgBase,
      color: T.textHi,
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, sans-serif",
      WebkitFontSmoothing: 'antialiased',
    }}>
      <Header />
      <main style={{ maxWidth: 1280, margin: '0 auto', padding: '24px 24px 80px', animation: 'risein .35s ease' }}>
        <Routes>
          <Route path="/" element={<Navigate to="/v2/dashboard/monsters" replace />} />
          <Route path="/dashboard" element={<Navigate to="/v2/dashboard/monsters" replace />} />
          <Route path="/dashboard/monsters" element={<TrendingV2 />} />
          <Route path="/dashboard/premarket" element={<PreMarketV2 />} />
          <Route path="/dashboard/social" element={<SocialV2 />} />
          <Route path="*" element={<Navigate to="/v2/dashboard/monsters" replace />} />
        </Routes>
      </main>
      <style>{`
        @keyframes risein { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes hglow {
          0%, 100% { box-shadow: 0 0 0 1px rgba(251,191,36,.4), 0 0 30px -6px rgba(251,191,36,.28); }
          50%      { box-shadow: 0 0 0 1px rgba(251,191,36,.55), 0 0 36px -4px rgba(251,191,36,.38); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        html, body { background: ${T.bgBase}; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-thumb { background: #222a38; border-radius: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
      `}</style>
    </div></SourcesProvider>
  );
}
